/**
 * THE NEW-PANE PAGE — what a pane shows before it has been told what it is for.
 *
 * ⌘T opens a space, a new space opens on one empty pane, and this is what that
 * pane draws. So it is the page you land on, and the page you land on had no
 * way to type. Its whole vocabulary was four muted rows at a sidebar's scale in
 * a window's worth of room, and the most common intent — start something —
 * meant pressing ⌘T and then immediately pressing ⌘K.
 *
 * ONE BOX. The middle of the page is the launcher (RFC 0333), the same
 * component ⌘K shows at compact size: a composer that is also the search
 * field. Ask is the first row, so Return sends what you typed as a new
 * session; ↓ and Return open a match, and Tab opens one from anywhere. A
 * second mode, or a `>` prefix, would ask the person to declare which one
 * they meant before they have finished thinking it.
 *
 * WHAT IT SHOWS AT REST is three tiles, not a list of everything: what is
 * running, what is waiting on you, then what you touched last. Everything
 * further away is one keystroke into the box, which is what Spotlight does and
 * why its home state is nearly empty. The tiles LEAVE when you type rather than
 * dimming behind a dropdown, so the page swaps contents instead of stacking a
 * layer over itself.
 *
 * It renders what it is given. Which items are recent, what is live, and what
 * happens when one is picked are the shell's business (WorldShell), because
 * "picked" means "this pane becomes that", which is a fact about the layout
 * tree.
 */

import type { AttachmentRef } from '@mod/core/model'
import { FileText, ListTodo, PanelLeftOpen, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { SessionMark } from '../../services/sessions/SessionMark'
import { Launcher, type LauncherComposerProps } from './Launcher'
import type { CommandTarget } from './launcherTargets'
import { Panel, PanelAction } from './Panel'

export interface NewPaneItem {
  id: string
  title: string
  /** Shown after the title: a path, usually. */
  hint?: string
  /** What the row is DOING, when that is worth saying: "waiting on you". Drawn
   *  as the tile's status line, so the grid reads as a status board rather than
   *  a history. */
  note?: string
  kind: 'document' | 'session' | 'task'
}

/** How many tiles the resting state shows. Three, because the fourth is always
 *  cheaper to reach by typing than by reading. */
const TILE_COUNT = 3

/** A heading and how many are under it: "Open 3". Sentence case, because an
 *  uppercase letterspaced label is a dashboard's voice and this is a page a
 *  person opens fifty times a day. */
function SectionHead({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-baseline gap-2 px-3 pt-3 pb-1.5">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-semibold text-foreground/80 text-sm">{count}</span>
    </div>
  )
}

/**
 * A tile: what the thing is doing, then what it is.
 *
 * The status leads because that is the question the page answers — a new pane
 * is opened to get back to the thing that needs you, and "waiting on you" is
 * the only word on the tile that could not be guessed from its title.
 */
function Tile({ item, onPick }: { item: NewPaneItem; onPick: () => void }) {
  const Icon = item.kind === 'task' ? ListTodo : FileText
  return (
    <button
      type="button"
      onClick={onPick}
      title={item.hint ?? item.title}
      className="flex h-[196px] min-w-0 flex-col rounded-[14px] border border-border bg-card p-5 text-left hover:border-foreground/20"
    >
      {/* A session wears its mark (RFC 0311), as its row in the sidebar and
          the `@` menu do, so the tile is recognisable before it is read. */}
      {item.kind === 'session' ? (
        <SessionMark id={item.id} size={25} className="shrink-0" />
      ) : (
        <Icon size={25} className="shrink-0 text-muted-foreground" />
      )}
      <div className="mt-auto flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
        {item.note && (
          <span
            aria-hidden
            className={`size-[7px] shrink-0 rounded-full ${
              item.note === 'waiting on you' ? 'bg-amber-500' : 'bg-emerald-400'
            }`}
          />
        )}
        <span className="truncate">
          {item.note ??
            (item.kind === 'session'
              ? 'Session'
              : item.kind === 'task'
                ? 'Open task'
                : 'Document')}
        </span>
      </div>
      <h3 className="line-clamp-2 pt-2.5 font-semibold text-[19px] text-foreground leading-tight">
        {item.title}
      </h3>
    </button>
  )
}

/** How many open tasks the card lists before "and N more". */
const TASK_ROWS = 3

/**
 * Pending work no session has taken (RFC 0304 3c), as a small card between the
 * composer and the recents. A row starts a session on that task. Kept apart
 * from the tiles: those are places you were, these are work nobody has begun.
 */
function OpenTasks({
  tasks,
  onPick,
}: {
  tasks: NewPaneItem[]
  onPick: (item: NewPaneItem) => void
}) {
  if (tasks.length === 0) return null
  const rest = tasks.length - TASK_ROWS
  return (
    <div className="mt-6 rounded-[14px] border border-border bg-card p-1.5">
      <SectionHead label="Open tasks" count={tasks.length} />
      {tasks.slice(0, TASK_ROWS).map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onPick(item)}
          className="flex w-full min-w-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[15px] hover:bg-foreground/[0.05]"
        >
          <ListTodo className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-foreground">
            {item.title}
          </span>
          {item.note ? (
            <span className="shrink-0 text-muted-foreground text-xs">
              {item.note}
            </span>
          ) : null}
        </button>
      ))}
      {rest > 0 ? (
        <p className="px-3 pt-1 pb-1.5 text-muted-foreground text-xs">
          and {rest} more
        </p>
      ) : null}
    </div>
  )
}

export function NewPanePanel({
  recent,
  sessions,
  tasks = [],
  targets = [],
  onPick,
  onPickTarget,
  onAsk,
  onAskWorld,
  worldName,
  onOpenUrl,
  composer,
  onSearch,
  onClose,
  sidebarCollapsed,
  onShowSidebar,
  focused = false,
}: {
  /** Documents and apps, most recently opened first. */
  recent: NewPaneItem[]
  /** Conversations that are still live, the ones needing you first. */
  sessions: NewPaneItem[]
  /** Pending project work no session has taken yet (RFC 0304 3c). */
  tasks?: NewPaneItem[]
  /** Everything this World can open or make, for the box to search. The same
   *  list ⌘K ranks, so the two never disagree about what exists. */
  targets?: CommandTarget[]
  onPick: (item: NewPaneItem) => void
  /** Open a searched result in this pane. */
  onPickTarget?: (target: CommandTarget) => void
  /**
   * Return on typed text: a session, started, with that text sent.
   *
   * Carries what the composer decided ALONGSIDE the text — the model, the
   * skills named with `/`, the files attached — because those are choices about
   * this message and are lost if only the string survives.
   */
  onAsk?: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /**
   * Open a typed URL as a web pane (RFC 0284), the way ⌘K's Enter does.
   *
   * A page whose box takes anything has to take a url: pasting one and being
   * told there are no matching documents is the box refusing the most obvious
   * thing in it. Offered as a row rather than done silently, because a session
   * whose first message is a link is also a reasonable thing to want, and the
   * two are told apart by which row you take.
   */
  onOpenUrl?: (url: string) => void
  /** What the World agent is called, for its row. */
  worldName?: string
  /** RFC 0336: send what is typed to the World agent, the row under Ask. */
  onAskWorld?: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /**
   * The rest of the real composer: model and harness, attachments, projects,
   * skills.
   *
   * PASSED THROUGH rather than reimplemented. This page used to draw its own
   * box, which meant the model picker, the `+`, the project capsule and the `/`
   * menu all existed one component away and none of them were here. A second
   * composer is a second set of everything, drifting from the first.
   */
  composer?: LauncherComposerProps
  /**
   * ⌘K, for the search this box cannot reach.
   *
   * New session and New terminal are NOT props any more. They were two of the
   * four rows this page used to be, and they are now two `create` targets among
   * the rest — the shell already mints them for ⌘K, including dropping the
   * terminal one on a machine that serves no shells (RFC 0276 §3). A page that
   * hard-coded two of them was choosing which two kinds mattered on behalf of a
   * World that can hold any number.
   */
  onSearch?: () => void
  onClose?: () => void
  sidebarCollapsed: boolean
  /** Absent on every pane but the one nearest the sidebar. */
  onShowSidebar?: () => void
  focused?: boolean
}) {
  const [query, setQuery] = useState('')
  const typing = query.trim().length > 0

  /** What the resting page shows: what needs you, then what you touched. */
  const tiles = useMemo(
    () => [...sessions, ...recent].slice(0, TILE_COUNT),
    [sessions, recent],
  )

  return (
    <Panel
      focused={focused}
      framed={false}
      padded={false}
      header={
        <>
          {sidebarCollapsed && onShowSidebar && (
            <PanelAction title="Show files (⌘B)" onClick={onShowSidebar}>
              <PanelLeftOpen size={14} />
            </PanelAction>
          )}
          <span className="truncate font-medium text-muted-foreground text-sm">
            New pane
          </span>
          <div className="flex-1" />
          {onSearch && (
            <PanelAction title="Search everything (⌘K)" onClick={onSearch}>
              <Search size={14} />
            </PanelAction>
          )}
          {onClose && (
            <PanelAction title="Close this pane" onClick={onClose}>
              <X size={14} />
            </PanelAction>
          )}
        </>
      }
    >
      <div className="@container h-full min-h-0 overflow-y-auto">
        {/* The wide treatment waits for @4xl (896px), not @2xl. A ⌘D half is
            about 700px, which cleared @2xl and got 120px gutters and three
            196px tiles in the space for one — every title ellipsed at two
            words. Three tiles need roughly 900px before they are tiles rather
            than a squeeze. */}
        <div className="mx-auto w-full max-w-[960px] px-6 pt-12 pb-10 @4xl:px-[120px] @4xl:pt-[120px]">
          {/* THE BOX. Nothing above it: no wordmark, no greeting, no heading.
              The window already says which World and space you are in, and a
              page that opens on a caret should not make you read past anything
              to reach it. */}
          {/* THE REAL COMPOSER, not a lookalike. Everything the box in a thread
              can do — pick a model and a harness, attach a file, name a skill
              with `/`, choose the project the work lands in — is here because
              it is the same component, and stays here as that component grows.
              A page that drew its own box had none of it and would have had to
              grow each one again. */}
          {/* `page` rather than the thread's `panel`: at the thread's scale
              this box reads as a toolbar that lost its thread. Its own slot
              provider, so wrapping it changes this composer and no other. */}
          <Launcher
            size="page"
            targets={targets}
            onPickTarget={onPickTarget}
            onAsk={onAsk}
            {...(onAskWorld ? { onAskWorldRow: onAskWorld } : {})}
            {...(worldName ? { worldName } : {})}
            onOpenUrl={onOpenUrl}
            composer={composer}
            onQueryChange={setQuery}
            placeholder={
              tasks.length === 0
                ? "What's on your mind?"
                : 'Ask anything, or open anything'
            }
            // Only the pane with focus takes the caret: two pickers side by
            // side would otherwise race for it on every render.
            autoFocus={focused}
            // Capped in container-query height rather than `vh`, because ⌘⇧D
            // splits a pane horizontally and the viewport is then not what
            // bounds this at all.
            listClassName="mt-3.5 max-h-[52cqh] overflow-y-auto rounded-[14px] border border-border bg-card p-1.5"
          />

          {typing ? null : tiles.length === 0 && tasks.length === 0 ? (
            <p className="px-1 pt-8 text-[15px] text-muted-foreground">
              Nothing open yet. Type to search, or press ⌘K.
            </p>
          ) : (
            <>
              <OpenTasks tasks={tasks} onPick={onPick} />
              {tiles.length > 0 ? (
                <>
                  <div className="flex items-baseline gap-2 px-1 pt-10 pb-3.5">
                    <span className="text-[15px] text-muted-foreground">
                      Pick up where you were
                    </span>
                    <span className="font-semibold text-[15px] text-foreground/80">
                      {tiles.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-5 @2xl:grid-cols-2 @4xl:grid-cols-3">
                    {tiles.map((item) => (
                      <Tile
                        key={`${item.kind}:${item.id}`}
                        item={item}
                        onPick={() => onPick(item)}
                      />
                    ))}
                  </div>
                  {/* The one line that says the rest exists. Without it three tiles
                  read as the whole of what this World has. */}
                  <div className="flex items-center justify-center gap-2.5 pt-7 text-[15px] text-muted-foreground">
                    Type to search everything else
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </Panel>
  )
}
